<template>
  <div>
    <b-modal
      ref="billGiftModal"
      hide-footer
      :title-html="modalTitle"
      @hidden="onModalHidden"
    >
      <v-form ref="form">
        <!-- Product Search Section -->
        <b-form-group label="Chọn sản phẩm quà tặng:">
          <vue-autosuggest
            v-model="searchText"
            :suggestions="filteredOptions"
            :loading="isSearching"
            @selected="onProductSelected"
            :limit="10"
            @input="onSearchInputChange"
            :input-props="searchInputProps"
            :should-render-suggestions="shouldRenderSuggestions"
          >
            <template #default="{ suggestion }">
              <ProductSuggestionItem :product="suggestion.item" />
            </template>
          </vue-autosuggest>
        </b-form-group>

        <!-- Selected Products Table -->
        <div class="mt-3">
          <h6>Sản phẩm đã chọn:</h6>
          <b-table
            :items="selectedProducts"
            :fields="tableFields"
            bordered
            hover
            small
            responsive
          >
            <template #cell(actions)="{ item }">
              <div class="d-flex justify-content-center">
                <b-button
                  variant="outline-danger btn-icon"
                  size="sm"
                  @click="removeProduct(item.id)"
                  v-b-tooltip.hover
                  title="Xóa"
                >
                  <v-icon small>mdi-delete</v-icon>
                </b-button>
              </div>
            </template>
          </b-table>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-3 column-gap">
          <b-button
            variant="secondary"
            size="sm"
            @click="hideModal"
            class="me-2"
          >
            Hủy
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="applyGifts"
            :disabled="selectedProducts.length === 0"
          >
            Áp dụng ({{ selectedProducts.length }})
          </b-button>
        </div>
      </v-form>
    </b-modal>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { convertPrice, makeToastFaile, makeToastSuccess } from '@/utils/common';
import { TIME_TRIGGER } from '@/utils/constants';
import { BILL_ITEM_TYPE, PRODUCT_TYPE_ENUM } from '@/utils/enum';
import ProductSuggestionItem from './ProductSuggestionItem.vue';
import productService from '../../../core/services/product';
import { ProductModel } from '../../../core/models';

export default {
  name: 'BillGiftModal',

  components: {
    ProductSuggestionItem,
  },

  props: {
    billItemId: {
      type: [String, null],
      required: true,
    },
    storeId: {
      type: [String, null],
      required: true,
    },
    productName: {
      type: [String, null],
      required: false,
    },
  },

  data() {
    return {
      modalTitle: '',
      searchText: '',

      selectedProducts: [],
      filteredOptions: [],
      isSearching: false,
      searchInputProps: {
        class: 'form-control',
        placeholder: 'Nhập tên sản phẩm để tìm kiếm...',
        autocomplete: 'off',
      },
    };
  },

  computed: {
    tableFields() {
      return [
        {
          key: 'index',
          label: 'STT',
          sortable: false,

          thStyle: this.headerStyle,
          tdClass: 'text-center',
          formatter: (value, key, item) => {
            return this.selectedProducts.findIndex((p) => p.id === item.id) + 1;
          },
        },
        {
          key: 'name',
          label: 'Sản phẩm',
          sortable: false,

          thStyle: this.headerStyle,
        },
        {
          key: 'quantityInStock',

          label: 'Tồn kho',
          sortable: false,

          thStyle: this.headerStyle,
          tdClass: 'text-end',
          formatter: (value) => value || 0,
        },
        {
          key: 'price',
          label: 'Giá bán',
          sortable: false,
          thStyle: this.headerStyle,
          tdClass: 'text-end',
          formatter: (value) => convertPrice(value),
        },

        {
          key: 'actions',
          label: '',
          thStyle: { ...this.headerStyle, width: '80px' },
        },
      ];
    },

    headerStyle() {
      return {
        fontWeight: 600,
        color: '#181c32',
        backgroundColor: '#f8f9fa',
      };
    },
  },

  methods: {
    convertPrice,

    shouldRenderSuggestions(size, loading) {
      return size >= 0 && !loading && this.searchText.length >= 2;
    },

    async onProductSelected(option) {
      try {
        const product = option.item;
        this.searchText = '';

        if (!this.isProductValid(product)) {
          return;
        }

        if (this.isProductAlreadySelected(product.id)) {
          makeToastFaile('Sản phẩm này đã được chọn!');
          return;
        }

        const giftProduct = this.createGiftProduct(product);
        this.selectedProducts.push(giftProduct);
      } catch (error) {
        console.error('Error selecting product:', error);
        makeToastFaile('Có lỗi xảy ra khi chọn sản phẩm!');
      }
    },

    isProductValid(product) {
      // Sản phẩm dịch vụ không cần kiểm tra tồn
      if (product.type === PRODUCT_TYPE_ENUM.SERVICE) {
        return true;
      }
      if (!product.isInStock()) {
        makeToastFaile('Số lượng sản phẩm tồn trong kho không đủ!');
        return false;
      }
      return true;
    },

    isProductAlreadySelected(productId) {
      return this.selectedProducts.some((p) => p.productId === productId);
    },

    createGiftProduct(product) {
      const productEntity = new ProductModel(product);
      return {
        id: uuidv4(),
        productId: productEntity.id,
        name: productEntity.name,
        productNameVat: productEntity.vatType || '',
        code: 'Quà tặng kèm',
        price: productEntity.sellingPrice || 0,
        importPrice: productEntity.importPrice || 0,
        quantity: 1,
        type: productEntity.type,
        totalPrice: productEntity.sellingPrice || 0,
        discount: 0,
        quantityInStock: productEntity.quantityInStock || 0,
        storeId: this.storeId,
        billItemType: BILL_ITEM_TYPE.PRODUCT_BONUS,
        checked: true,
        isGetVat: false,
        belongBillDetailId: this.billItemId,
      };
    },

    onSearchInputChange(text) {
      this.searchText = (text || '').trim();
      this.debouncedSearch();
    },

    debouncedSearch: debounce(async function () {
      if (this.searchText.length < 2) {
        this.filteredOptions = [];
        return;
      }

      await this.searchProducts();
    }, TIME_TRIGGER),

    async searchProducts() {
      if (!this.searchText || this.isSearching) return;

      try {
        this.isSearching = true;

        const response = await productService.searchProducts({
          storeId: this.storeId,
          productName: this.searchText,
          isCheckStock: 1,
        });

        const products = response.data?.data || [];
        this.filteredOptions = [
          {
            data: ProductModel.fromJsonArray(products),
          },
        ];
      } catch (error) {
        console.error('Error searching products:', error);
        this.filteredOptions = [];
        makeToastFaile('Có lỗi xảy ra khi tìm kiếm sản phẩm!');
      } finally {
        this.isSearching = false;
      }
    },

    removeProduct(productId) {
      this.selectedProducts = this.selectedProducts.filter(
        (p) => p.id !== productId,
      );
    },

    showModal() {
      this.resetModal();
      this.generateModalTitle();
      this.$refs.billGiftModal.show();
    },

    hideModal() {
      this.$refs.billGiftModal.hide();
    },

    onModalHidden() {
      this.resetModal();
    },

    resetModal() {
      this.searchText = '';
      this.selectedProducts = [];
      this.filteredOptions = [];
      this.isSearching = false;
    },

    generateModalTitle() {
      this.modalTitle = `
		  <div>
			<span class='d-block' style='font-size: 1.1rem'>
			  Thêm quà tặng cho sản phẩm:
			  <span class='text-primary'>#${this.productName}</span>
			</span>
		  </div>
		`;
    },

    applyGifts() {
      if (this.selectedProducts.length === 0) {
        makeToastFaile('Vui lòng chọn ít nhất một sản phẩm quà tặng!');
        return;
      }

      const giftList = this.selectedProducts;

      this.$emit('addGift', giftList);
      this.hideModal();
      makeToastSuccess(`Đã thêm ${giftList.length} sản phẩm quà tặng!`);
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}

.autosuggest__results {
  position: absolute;
  width: 100%;

  max-height: 300px;
  z-index: 1050;
  border: 1px solid #e0e0e0;
  border-radius: 0.375rem;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.autosuggest__results-container {
  position: relative;
  width: 100%;
}

.autosuggest__input {
  display: block;
  width: 100%;
  height: calc(1.35em + 1.1rem + 2px);
  line-height: 1.35;
  padding: 0.55rem 0.75rem;
  font-weight: 400;
  font-size: 0.925rem;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.autosuggest__input:focus {
  outline: none !important;
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.autosuggest__results-item:hover {
  background-color: #f8f9fa;
}

.autosuggest__results ul li {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}

.autosuggest__results ul li:last-child {
  border-bottom: none;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background-color: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background-color: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #a8a8a8;
}

.column-gap {
  column-gap: 10px;
}
</style>
